import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import {
  DD_ENABLED,
  DD_APPLICATION_ID,
  DD_CLIENT_TOKEN,
  DD_SITE,
  DD_SERVICE,
  IS_UI_TEST_BUILD
} from 'core/constants';

/** Module-level flag; only true after a successful datadogRum.init() call. */
let active = false;

export const isDatadogActive = (): boolean => active;

/**
 * Initialise Datadog RUM. Safe to call exactly once, at startup, before render.
 *
 * No-op (never imports/calls datadogRum.init) when: this is a UI-test or Jest build, Datadog
 * is not opted in, or credentials are missing — a half-configured env disables the integration
 * rather than initialising a broken client. Never throws: telemetry failing must not block boot.
 */
export function initDatadogRum(): boolean {
  if (
    IS_UI_TEST_BUILD ||
    process.env.NODE_ENV === 'test' ||
    !DD_ENABLED ||
    !DD_APPLICATION_ID ||
    !DD_CLIENT_TOKEN
  ) {
    return false;
  }

  try {
    datadogRum.init({
      applicationId: DD_APPLICATION_ID,
      clientToken: DD_CLIENT_TOKEN,
      site: DD_SITE,
      service: DD_SERVICE,
      env: process.env.REACT_APP_ENV_NAME,
      version: process.env.REACT_APP_VERSION,
      sessionSampleRate: 100,
      // Session Replay bills separately from RUM and captures rendered PII (addresses, names,
      // home details) that `defaultPrivacyLevel: 'mask-user-input'` does NOT cover — that option
      // only masks form inputs, not read-only display text. Off until replay's privacy review is
      // done, mirroring the mvp app's same decision (src/core/datadog/datadogClient.ts).
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input'
    });
    active = true;
  } catch (error) {
    console.warn('Datadog RUM init failed; continuing without it.', error);
    active = false;
    return false;
  }

  // Separate product/SDK from RUM: RUM only auto-captures uncaught exceptions, not console.error
  // calls or arbitrary structured log events. forwardErrorsToLogs mirrors console.error/window.onerror
  // into Logs so error-only console output is searchable there without instrumenting every call site.
  try {
    datadogLogs.init({
      clientToken: DD_CLIENT_TOKEN,
      site: DD_SITE,
      service: DD_SERVICE,
      env: process.env.REACT_APP_ENV_NAME,
      version: process.env.REACT_APP_VERSION,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100
    });
  } catch (error) {
    console.warn('Datadog Logs init failed; continuing without it.', error);
  }

  return true;
}
